import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';

import { EcmDispatcherModule } from '@ecm/dispatcher';
import { Contract, ContractsBusinessModel, PortfolioService } from '@ecm/portfolio';
import { ErrorPageService, RouterService } from '@ecm/router';

@Injectable({
    providedIn: 'root',
})
export class BillsGuard {
    private static readonly CLASS_NAME: string = 'BillsGuard';

    private readonly portfolioService = inject(PortfolioService);
    private readonly routerService = inject(RouterService);
    private readonly errorPageService = inject(ErrorPageService);

    public canActivate(): Observable<boolean> {
        return this.portfolioService.getRawContracts().pipe(
            map((contractsBusinessModel: ContractsBusinessModel) => {
                // filter contracts
                const contracts: Contract[] = contractsBusinessModel.contracts.filter((contract: Contract) => {
                    if (contract.telco && contract.telco.state) {
                        const telcoState: string = contract.telco.state.toLowerCase();
                        return telcoState === 'logged' || telcoState === 'held' || telcoState === 'delegated';
                    } else if (contract.security) {
                        return true;
                    }
                });

                // check user contracts and set flags
                let hasSecurity = false;
                let hasProObs = false;
                let hasParnasse = false;
                contracts.forEach((contract: Contract) => {
                    if (contract.security) {
                        hasSecurity = true;
                    } else if (
                        contract.telco &&
                        contract.telco.subType &&
                        contract.telco.subType.toLowerCase() === 'parnasse'
                    ) {
                        hasParnasse = true;
                    } else if (contract.telco && contract.telco.marketType) {
                        const marketType: string = contract.telco.marketType.toLowerCase();
                        if (marketType === 'pro' || marketType === 'obs') {
                            hasProObs = true;
                        }
                    }
                });

                let canActivate = false;
                // compute canActivate with special cases
                if (hasSecurity || hasProObs || hasParnasse) {
                    // allow security|pro|obs|parnasse users
                    EcmDispatcherModule.sendConsoleInfo(
                        BillsGuard.CLASS_NAME,
                        'User has at least 1 security|pro/obs|parnasse contract',
                    );
                    canActivate = true;
                } else if (contracts.length > 1) {
                    // allow multi-contracts users
                    EcmDispatcherModule.sendConsoleInfo(BillsGuard.CLASS_NAME, 'User has more than 1 telco contract');
                    canActivate = true;
                } else if (contracts.length === 1 && contracts[0].fictif && contracts[0].fictif === true) {
                    // allow fictif users
                    canActivate = true;
                }

                if (canActivate) {
                    const billsHP = 'facture-paiement';
                    this.routerService.navigate([billsHP]);
                    EcmDispatcherModule.sendConsoleInfo(
                        BillsGuard.CLASS_NAME,
                        'User is allowed, will let go to route ' + billsHP,
                    );
                } else {
                    if (contracts.length === 1) {
                        // redirect to bills HP for basic mono-contract case
                        // (except for `fictif` contracts, see above)
                        const contract: Contract = contracts[0];
                        const billsHP: string = 'facture-paiement/' + contract.cid;
                        EcmDispatcherModule.sendConsoleError(
                            BillsGuard.CLASS_NAME,
                            'User is not allowed, will redirect to route ' + billsHP,
                        );
                        this.routerService.navigate([billsHP]);
                    } else {
                        EcmDispatcherModule.sendConsoleError(
                            BillsGuard.CLASS_NAME,
                            'User has no contracts, will redirect to default error page',
                        );
                        this.errorPageService.showDefaultErrorPage();
                    }
                }

                return canActivate;
            }),
        );
    }
}
